import * as React from 'react';
import type { Highlight, PolygonHighlight, RectangleHighlight } from '../types';
import { pointsToSvg } from '../helpers/coords';
import { isOwnHighlight } from '../helpers/isOwnHighlight';
import { noteColorForHighlight } from '../helpers/noteColor';

function noteLabel(h: Highlight): number | null {
    if (h.detail?.noteId != null) return h.detail.noteId;
    if (/^\d+-\d+$/.test(h.id)) return parseInt(h.id, 10);
    return null;
}

const labelStyle: React.CSSProperties = { pointerEvents: 'none', userSelect: 'none' };

interface SvgAnnotationLayerProps {
    highlights: Highlight[];
    pageNumber: number;
    dimension: { width: number; height: number };
    polygonMode: boolean;
    moveMode: boolean;
    moveDragRef: React.MutableRefObject<any>;
    pdfLayersRef: React.RefObject<HTMLDivElement | null>;
    draftPoints: Array<{ x: number; y: number }>;
    freehandPolylineRef: React.RefObject<SVGPolylineElement | null>;
    freehandStartCircleRef: React.RefObject<SVGCircleElement | null>;
    rectDraft: { x1: number; y1: number; x2: number; y2: number } | null;
    onPopupForPolygon: (h: Highlight) => void;
    onPopupForRect: (h: Highlight) => void;
    onScheduleClose: () => void;
    onOpenEditModal: (h: Highlight) => void;
    onMoveBlocked?: () => void;
    canMove?: (h: Highlight) => boolean;
}

export function SvgAnnotationLayer({
    highlights,
    pageNumber,
    dimension,
    polygonMode,
    moveMode,
    moveDragRef,
    pdfLayersRef,
    draftPoints,
    freehandPolylineRef,
    freehandStartCircleRef,
    rectDraft,
    onPopupForPolygon,
    onPopupForRect,
    onScheduleClose,
    onOpenEditModal,
    onMoveBlocked,
    canMove,
}: SvgAnnotationLayerProps) {
    const { width: dw, height: dh } = dimension;

    function startMove(e: React.MouseEvent, id: string, moveType: 'positioned' | 'polygon' | 'rect', extra: any = {}) {
        if (!moveMode) return;
        e.preventDefault();
        e.stopPropagation();
        moveDragRef.current = {
            id,
            el: e.currentTarget,
            type: moveType,
            startNX: (e.clientX - pdfLayersRef.current!.getBoundingClientRect().left) / dw,
            startNY: (e.clientY - pdfLayersRef.current!.getBoundingClientRect().top) / dh,
            ...extra,
        };
        document.body.style.cursor = 'grabbing';
    }

    return (
        <svg
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none', zIndex: 8 }}
        >
            {highlights
                .filter((h): h is PolygonHighlight => h.pageNumber === pageNumber && h.type === 'polygon')
                .map((h) => {
                    const lbl = noteLabel(h);
                    const c = noteColorForHighlight(isOwnHighlight(h), h.persistStatus);
                    const movable = canMove?.(h) ?? isOwnHighlight(h);
                    const cx = h.points.reduce((s, p) => s + p.x, 0) / h.points.length * dw;
                    const cy = h.points.reduce((s, p) => s + p.y, 0) / h.points.length * dh;
                    return (
                        <React.Fragment key={h.id}>
                            <polygon
                                data-marker-id={h.id}
                                points={pointsToSvg(h.points, dw, dh)}
                                fill={c.fill}
                                stroke={c.stroke}
                                strokeWidth="1.5"
                                style={{ pointerEvents: 'all', cursor: moveMode && movable ? 'move' : 'pointer' }}
                                onMouseDown={(e) => { if (!moveMode) return; if (!movable) { onMoveBlocked?.(); return; } startMove(e, h.id, 'polygon', { origPoints: h.points }); }}
                                onMouseEnter={() => { if (!moveMode) onPopupForPolygon(h); }}
                                onMouseLeave={onScheduleClose}
                                onClick={(e) => { if (!moveMode) { e.stopPropagation(); onOpenEditModal(h); } }}
                            />
                            {lbl != null && (
                                <text x={cx} y={cy}
                                    data-label-for={h.id}
                                    textAnchor="middle" dominantBaseline="middle"
                                    fontSize={12} fontWeight="bold" fill="white"
                                    stroke={c.text} strokeWidth={5} paintOrder="stroke"
                                    style={labelStyle}>
                                    {lbl}
                                </text>
                            )}
                        </React.Fragment>
                    );
                })}

            {polygonMode && draftPoints.length >= 3 && (
                <polygon
                    points={pointsToSvg(draftPoints, dw, dh)}
                    fill="rgba(251,191,36,0.2)"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                    style={{ pointerEvents: 'none' }}
                />
            )}
            {polygonMode && draftPoints.length >= 2 && (
                <polyline
                    points={pointsToSvg(draftPoints, dw, dh)}
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                    style={{ pointerEvents: 'none' }}
                />
            )}
            {polygonMode && draftPoints.map((p, i) => (
                <circle
                    key={i}
                    cx={p.x * dw}
                    cy={p.y * dh}
                    r={i === 0 ? 6 : 4}
                    fill={i === 0 ? '#f59e0b' : '#fff'}
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    style={{ pointerEvents: 'none' }}
                />
            ))}

            <polyline
                ref={freehandPolylineRef}
                points=""
                fill="rgba(251,191,36,0.15)"
                stroke="#f59e0b"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                style={{ pointerEvents: 'none' }}
            />
            <circle
                ref={freehandStartCircleRef}
                r={6}
                fill="#f59e0b"
                stroke="#fff"
                strokeWidth="1.5"
                visibility="hidden"
                style={{ pointerEvents: 'none' }}
            />

            {highlights
                .filter((h): h is RectangleHighlight => h.pageNumber === pageNumber && h.type === 'rect')
                .map((h) => {
                    const x = Math.min(h.x1, h.x2) * dw;
                    const y = Math.min(h.y1, h.y2) * dh;
                    const w = Math.abs(h.x2 - h.x1) * dw;
                    const h2 = Math.abs(h.y2 - h.y1) * dh;
                    const lbl = noteLabel(h);
                    const c = noteColorForHighlight(isOwnHighlight(h), h.persistStatus);
                    const movable = canMove?.(h) ?? isOwnHighlight(h);
                    return (
                        <React.Fragment key={h.id}>
                            <rect
                                data-marker-id={h.id}
                                x={x}
                                y={y}
                                width={w}
                                height={h2}
                                fill={c.fill}
                                stroke={c.stroke}
                                strokeWidth="1.5"
                                style={{ pointerEvents: 'all', cursor: moveMode && movable ? 'move' : 'pointer' }}
                                onMouseDown={(e) => { if (!moveMode) return; if (!movable) { onMoveBlocked?.(); return; } startMove(e, h.id, 'rect', { origX1: h.x1, origY1: h.y1, origX2: h.x2, origY2: h.y2 }); }}
                                onMouseEnter={() => { if (!moveMode) onPopupForRect(h); }}
                                onMouseLeave={onScheduleClose}
                                onClick={(e) => { if (!moveMode) { e.stopPropagation(); onOpenEditModal(h); } }}
                            />
                            {lbl != null && (
                                <text x={x + w / 2} y={y + h2 / 2}
                                    data-label-for={h.id}
                                    textAnchor="middle" dominantBaseline="middle"
                                    fontSize={12} fontWeight="bold" fill="white"
                                    stroke={c.text} strokeWidth={5} paintOrder="stroke"
                                    style={labelStyle}>
                                    {lbl}
                                </text>
                            )}
                        </React.Fragment>
                    );
                })}

            {rectDraft && (
                <rect
                    x={Math.min(rectDraft.x1, rectDraft.x2) * dw}
                    y={Math.min(rectDraft.y1, rectDraft.y2) * dh}
                    width={Math.abs(rectDraft.x2 - rectDraft.x1) * dw}
                    height={Math.abs(rectDraft.y2 - rectDraft.y1) * dh}
                    fill="rgba(251,191,36,0.2)"
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                    style={{ pointerEvents: 'none' }}
                />
            )}
        </svg>
    );
}
